import React, { FC, useEffect } from 'react';
import { dmsService, analyticsService } from '@web/services/singletons';
import { getDisplayDate, getDisplayValue, getTimeAgoString } from '@web/utils';
import { useTranslation } from 'react-i18next';
import { DATE_FORMAT } from '@web/config/constants';
import { DataCard } from '@web/components/data-card';
import { useParams } from 'react-router-dom';
import { BmisBatteryBadge } from '@web/bmis/components/bmis-battery-badge';
import { useDmsDispatch, useDmsState } from '@packages/contexts/dms';
import { ANALYTICS_EVENTS } from '@packages/core/analytics';

export const DmsSummaryCard: FC = () => {
    const { vin } = useParams<{ vin: string }>();
    const { loading, latestRepairOrder, hasFetched } = useDmsState();
    const dispatch = useDmsDispatch();
    const { t } = useTranslation();

    useEffect(() => {
        if (!vin) return;

        if (!hasFetched) {
            dmsService.fetchAndSetLatestRepairOrder(vin, dispatch);
        }
    }, [vin, latestRepairOrder, hasFetched]);

    const dmsData = React.useMemo(() => {
        if (!hasFetched || !latestRepairOrder || (latestRepairOrder && Object.keys(latestRepairOrder).length === 0))
            return [];
        return [
            { label: t('RO Number'), value: getDisplayValue(latestRepairOrder?.roNumber) },
            {
                label: t('Closed Date'),
                value: getDisplayDate(latestRepairOrder?.closedDate, DATE_FORMAT.MMM_D_YEAR),
            },
            { label: t('Car Mileage'), value: `${getDisplayValue(latestRepairOrder?.mileage)} mi` },
            {
                value:
                    Array.isArray(latestRepairOrder?.details) && latestRepairOrder.details.length > 0
                        ? (latestRepairOrder.details[0]?.correction ?? 'N/A')
                        : 'N/A',
            },
        ];
    }, [latestRepairOrder, t, hasFetched]);

    const headerBadgeText = React.useMemo(() => {
        if (!hasFetched || !latestRepairOrder || (latestRepairOrder && Object.keys(latestRepairOrder).length === 0))
            return '';
        return getTimeAgoString(latestRepairOrder?.closedDate ?? '');
    }, [latestRepairOrder, hasFetched]);

    return (
        <DataCard
            footerButtonClickCallback={() => {
                analyticsService.logEvent(ANALYTICS_EVENTS.VIN_SESSION_VEHICLE_CARD_CLICKED, {
                    cardType: 'ro_information',
                    roNumber: latestRepairOrder?.roNumber,
                    moduleName: 'vin_session',
                    sourceModule: 'vin_session',
                });
            }}
            data={dmsData}
            footerButtonText="View this RO"
            footerButtonLink={`./repair-orders/${dmsData?.[0]?.value}`}
            headerText="Latest Repair Order"
            headerBadge={<BmisBatteryBadge label={headerBadgeText} />}
            loading={loading}
            isEmpty={
                loading
                    ? false
                    : Boolean(!latestRepairOrder || (latestRepairOrder && Object.keys(latestRepairOrder).length === 0))
            }
            emptyTitle="There is no Repair Order for this Car"
            emptyDescription="A Repair Order will be available once the vehicle undergoes service."
        />
    );
};
